import { clientAgentId } from 'constants/history';
import { AgentType } from 'enums/AgentType';
import { ChatViewMode } from 'enums/ChatViewMode';
import { DefaultRootState } from 'react-redux';
import { _counterLog } from 'utils/_counterLog';
import { isMessageReadable, sortHistoryMessages } from 'utils/messages';

export const selectIsDialogOpen = (state: DefaultRootState) => {
    return state.history?.chatNow ?? false;
};

export const selectIsOperator = (agentId?: string, agentType?: AgentType) => {
    const targets = [AgentType.Operator, AgentType.Manager];
    return (state: DefaultRootState) => {
        if (!agentId) {
            return false;
        }

        if (agentType) {
            return targets.includes(agentType);
        }

        return (
            String(state.history.lastAgent?.id) === String(agentId) &&
            targets.includes(state.history.lastAgent?.type)
        );
    };
};

export const selectLastMessage = ({
    history: {
        bubbles,
        clientSideMessages,
        messages,
        historyEndMsgId,
        currentBubbleIndex,
        favoritesMode,
    },
    chat: { viewMode },
}: DefaultRootState) => {
    const isLastBubble =
        currentBubbleIndex === Math.max(0, bubbles.length - 1) &&
        historyEndMsgId !== String(Number.MAX_SAFE_INTEGER);

    const injectClientSideMessages = isLastBubble && viewMode === ChatViewMode.Normal;

    let sortedHistory = sortHistoryMessages([
        ...(injectClientSideMessages ? Object.values(clientSideMessages) : []),
        ...(bubbles[bubbles.length - 1]?.map((id) => messages[id]) ?? []),
    ]);

    if (favoritesMode) {
        sortedHistory = sortedHistory.filter((msg) => msg.favorite);
    }

    return sortedHistory[sortedHistory.length - 1];
};

export const selectUnreadCount = ({
    history: { messages, lastReadByClientMessageId },
}: DefaultRootState) => {
    let unreadCount = 0;

    Object.values(messages).forEach((msg) => {
        const isReadable = isMessageReadable(msg);
        if (
            isReadable &&
            !msg.clientSide &&
            msg.agentId !== clientAgentId &&
            Number(msg.id) > Number(lastReadByClientMessageId)
        ) {
            _counterLog.log(
                `selectUnreadCount`,
                `msg.text => ${msg.text}\n\r
                [msg.id, lastReadByClientMessageId] => [${msg.id}, ${lastReadByClientMessageId}]`,
            );
            unreadCount++;
        }
    });

    return unreadCount;
};

export const selectMessageBy = (messageId) => {
    return (state: DefaultRootState) => state.history.messages?.[messageId];
};

export const selectFollowedMessageCouple = (state: DefaultRootState) => {
    const lastVisibleMessageId = state.history.visibleRange[1];
    const followedMessageIndex = state.history.followedMessageIds.findIndex((id) => {
        return Number(id) > Number(lastVisibleMessageId);
    });
    const followedMessageId = state.history.followedMessageIds?.[followedMessageIndex];
    return [followedMessageId, followedMessageIndex] as [string, number];
};
